'use client';

import { useEffect, useRef, useState } from 'react';
import * as CookieConsent from 'vanilla-cookieconsent';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { TourTabHeading } from '../tour-content';
import type { TourVideo } from '../../../../utils/tour-videos';

export function VideosTab({
  videos,
  tourName,
  image,
}: {
  videos: TourVideo[];
  tourName: string;
  image?: string;
}) {
  const { t } = useTranslations();
  const locale = useLocaleStore(state => state.currentLocale);
  const cs = locale === 'cs';
  const [allowed, setAllowed] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const player = useRef<HTMLIFrameElement>(null);
  const buttons = useRef<Record<string, HTMLButtonElement | null>>({});
  const copy = {
    empty: cs
      ? 'K tomuto zájezdu zatím nejsou přidaná videa.'
      : 'K tomuto zájazdu zatiaľ nie sú pridané videá.',
    play: cs ? 'Přehrát' : 'Prehrať',
    stop: cs ? 'Zavřít video' : 'Zavrieť video',
    consent: cs
      ? 'Pro náhledy a přehrávání YouTube videí povolte funkční cookies.'
      : 'Pre náhľady a prehrávanie YouTube videí povoľte funkčné cookies.',
    settings: cs ? 'Nastavení cookies' : 'Nastavenia cookies',
    external: cs ? 'Otevřít na YouTube (nová karta)' : 'Otvoriť na YouTube (nová karta)',
  };

  useEffect(() => {
    const sync = () => {
      const accepted = CookieConsent.acceptedCategory('functional');
      setAllowed(accepted);
      if (!accepted) setActiveId(null);
    };
    sync();
    const events = ['cc:onConsent', 'cc:onChange', 'cc:onReady'];
    events.forEach(event => window.addEventListener(event, sync));
    return () => events.forEach(event => window.removeEventListener(event, sync));
  }, []);

  useEffect(() => {
    if (activeId) player.current?.focus();
  }, [activeId]);

  const preferences = () =>
    window.dispatchEvent(new CustomEvent('eliadmin:open-cookie-preferences'));

  return (
    <div className="flex w-full flex-col items-start justify-start gap-6 lg:gap-8">
      <TourTabHeading title={t('text3959')} />
      {!videos.length ? (
        <p>{copy.empty}</p>
      ) : (
        <>
          {!allowed && (
            <p>
              {copy.consent}{' '}
              <button type="button" className="underline" onClick={preferences}>
                {copy.settings}
              </button>
            </p>
          )}
          <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {videos.map(video => {
              const title = `${tourName} – ${video.name}`;
              const playing = allowed && activeId === video.id;
              return (
                <article key={video.id} className="flex min-w-0 flex-col gap-3">
                  <div className="aspect-video w-full overflow-hidden rounded-xl bg-secondary">
                    {playing ? (
                      <iframe
                        ref={player}
                        src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0`}
                        title={title}
                        tabIndex={0}
                        className="h-full w-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      />
                    ) : (
                      <button
                        ref={element => {
                          buttons.current[video.id] = element;
                        }}
                        type="button"
                        className="relative flex h-full w-full items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px]"
                        aria-label={`${copy.play}: ${title}`}
                        onClick={() => (allowed ? setActiveId(video.id) : preferences())}
                      >
                        <VideoThumbnail
                          key={`${video.id}:${allowed}`}
                          src={allowed ? `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg` : image}
                        />
                        <span
                          className="absolute rounded-full bg-primary px-5 py-3 text-primary-foreground"
                          aria-hidden="true"
                        >
                          ▶ {copy.play}
                        </span>
                      </button>
                    )}
                  </div>
                  <h3 className="font-bold">{title}</h3>
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    {playing && (
                      <button
                        type="button"
                        className="underline"
                        onClick={() => {
                          setActiveId(null);
                          requestAnimationFrame(() => buttons.current[video.id]?.focus());
                        }}
                      >
                        {copy.stop}
                      </button>
                    )}
                    <a
                      href={`https://www.youtube.com/watch?v=${video.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                      aria-label={`${title}: ${copy.external}`}
                    >
                      {copy.external}
                    </a>
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function VideoThumbnail({ src }: { src?: string }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return null;
  return (
    <Image
      src={src}
      alt=""
      width={480}
      height={270}
      sizes="(min-width: 1280px) 33vw, (min-width: 640px) 50vw, 100vw"
      loading="lazy"
      className="h-full w-full object-cover"
      onError={() => setFailed(true)}
    />
  );
}
